import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { enterUpDelayed } from '../../utils/animations';

const intro = 'White Glove Hiring, LLC ("White Glove Hiring," "we," "us," or "our") is a Washington limited liability company that helps businesses hire, onboard, and manage virtual assistants. This policy explains what information we collect, how we use and share it, and the choices you have.';

const sections = [
    {
        title: '1. Who This Policy Covers',
        blocks: [
            { type: 'p', text: 'This policy applies to:' },
            { type: 'ul', items: [
                'Visitors to whiteglovehiring.com',
                'Prospective and current clients',
                'Business professionals we reach out to',
                'Virtual assistant candidates',
                'Virtual assistants placed with our clients',
                'Referral partners',
            ] },
        ],
    },
    {
        title: '2. Information We Collect',
        blocks: [
            { type: 'ul', items: [
                'Website visitors: device and usage information, such as browser type, pages visited, and time on site, collected through cookies, analytics tools, and embedded video and scheduling tools.',
                'Prospective and current clients: name, email address, phone number, business name, business and team details, hiring needs, agreement details, and billing information. Payments are processed by a third-party payment processor. We do not store full card or bank account numbers.',
                'Business professionals we reach out to: business contact information such as name, company or brokerage, business phone number, and business email, obtained from public sources such as brokerage websites and business directories, and from business contact lists.',
                "Virtual assistant candidates: name, contact details, location, resume, work history, references, skills and behavioral assessment results, interview responses, interview recordings, and compensation expectations. We verify candidates' work history and references during pre-interview screening. These are employment verification checks, not government or criminal background checks.",
                "Placed virtual assistants: timeclock records, activity data, and periodic screenshots captured through our monitoring platform during working hours, plus performance and satisfaction feedback. Screenshots may capture information shown on the assistant's screen, including information belonging to our clients and their customers. We treat all monitoring data as confidential.",
                'Referral partners: contact details, referral activity, payout details, and the tax information we need to issue required tax forms.',
            ] },
        ],
    },
    {
        title: '3. How We Use Information',
        blocks: [
            { type: 'ul', items: [
                'To respond to inquiries and to schedule and conduct calls',
                'To provide our hiring, onboarding, monitoring, and support services',
                'To reach out to business professionals who may benefit from our services',
                'To source, screen, assess, and match candidates with client roles',
                'To track virtual assistant time and activity and report findings to clients',
                'To process payments, referral commissions, and tax reporting',
                'To send service communications, such as appointment reminders, interview details, and onboarding updates',
                'To send newsletters and business updates, which you can opt out of at any time',
                'To operate and improve our website and services',
                'To comply with the law, enforce our agreements, and prevent fraud',
            ] },
        ],
    },
    {
        title: '4. Use of AI Tools',
        blocks: [
            { type: 'p', text: 'We use AI-assisted tools to help source and screen candidates, for example to review resumes and summarize interviews. Our team reviews these results, and final hiring decisions are made by our clients, not by automated systems.' },
        ],
    },
    {
        title: '5. Call and Interview Recordings',
        blocks: [
            { type: 'p', text: 'We may record calls and video interviews for quality, training, and to share with clients considering a candidate. We will tell you before a recording begins.' },
        ],
    },
    {
        title: '6. Phone Calls and Outreach',
        blocks: [
            { type: 'p', text: 'We call business professionals, such as real estate agents and team leaders, who we believe may benefit from our services. We obtain their business contact information from public sources, such as brokerage websites and business directories, and from business contact lists. Every call is placed by a member of our team. We do not use prerecorded or artificial voice messages.' },
            { type: 'p', text: 'If you ask us not to call you again, we will add your number to our internal do-not-call list and stop calling you. You can also make this request at any time by emailing adam@whiteglovehiring.com.' },
            { type: 'p', text: 'We do not send text messages to anyone who has not agreed to receive them.' },
        ],
    },
    {
        title: '7. SMS Text Messaging',
        blocks: [
            { type: 'p', text: 'By providing your phone number and agreeing to receive text messages from White Glove Hiring, you consent to receive SMS messages about scheduled calls, interviews, onboarding, and the services we provide to you.' },
            { type: 'ul', items: [
                'Message frequency varies.',
                'Message and data rates may apply.',
                'Reply STOP at any time to opt out. You will receive one confirmation message and no further texts.',
                'Reply HELP for help, or email adam@whiteglovehiring.com.',
                'Consent to receive text messages is not a condition of purchasing any service.',
                'Carriers are not liable for delayed or undelivered messages.',
            ] },
        ],
    },
    {
        title: '8. Email Communications',
        blocks: [
            { type: 'p', text: 'We send service emails related to your inquiry, application, or account, and we may send newsletters and business updates. Every marketing email includes an unsubscribe link. Service emails continue while you are an active client, candidate, or partner. We may also email business professionals who may benefit from our services. Every one of these emails includes a way to opt out, and we honor opt-out requests promptly.' },
        ],
    },
    {
        title: '9. How We Share Information',
        blocks: [
            { type: 'p', text: 'We do not sell your personal information. We share information only in these limited cases:' },
            { type: 'ul', items: [
                'Service providers that help us run our business, including scheduling, video conferencing, phone and text messaging, email and newsletter, customer relationship management, payment processing, time and activity monitoring, AI, and website hosting providers. They may use your information only to perform services for us.',
                'Clients: we share candidate application materials, assessment results, and interview recordings with clients considering that candidate. We share monitoring data and reports about a placed virtual assistant with the client that assistant works for.',
                "Candidates: we share relevant role and business details with candidates being considered for a client's role.",
                'Business transfers: if White Glove Hiring is involved in a merger, acquisition, or sale of assets, information may be transferred as part of that transaction.',
                'Legal requirements: when required by law or to protect our legal rights.',
            ] },
            { type: 'p', text: 'No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.' },
        ],
    },
    {
        title: '10. International Data Transfers',
        blocks: [
            { type: 'p', text: 'Our team, candidates, and virtual assistants are located in several countries, including outside the United States. Your information may be accessed, processed, and stored in countries other than your own, where data protection laws may differ. We take reasonable steps to protect your information wherever it is processed.' },
        ],
    },
    {
        title: '11. Cookies and Tracking',
        blocks: [
            { type: 'p', text: 'We use cookies and similar technologies to operate our website, understand how it is used, and, where used, measure and improve our advertising. Some of these tools are provided by third parties, such as analytics and advertising platforms, which may collect information about your visit. You can control cookies through your browser settings. Our website does not currently respond to Do Not Track browser signals.' },
        ],
    },
    {
        title: '12. Data Security',
        blocks: [
            { type: 'p', text: 'We take reasonable measures to protect personal information from unauthorized access, disclosure, or misuse. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.' },
        ],
    },
    {
        title: '13. Data Retention',
        blocks: [
            { type: 'p', text: 'We keep personal information only as long as needed for the purposes in this policy, to meet legal, tax, and contractual obligations, and to resolve disputes. If you are a candidate who is not selected, we may keep your application to consider you for future roles unless you ask us to delete it. Monitoring data is kept as long as needed to provide our services and as described in our client agreements.' },
        ],
    },
    {
        title: '14. Your Choices and Rights',
        blocks: [
            { type: 'p', text: 'You may ask to access, correct, or delete your personal information at any time. You may opt out of text messages by replying STOP, unsubscribe from marketing emails using the link in any email, and ask us to stop calling you, which adds your number to our internal do-not-call list. We may need to keep some information to meet legal or contractual obligations. Depending on where you live, you may have additional rights under local law. We will not treat you differently for exercising your rights.' },
        ],
    },
    {
        title: "15. Children's Privacy",
        blocks: [
            { type: 'p', text: 'Our website and services are intended for businesses and adults. We do not knowingly collect personal information from anyone under 18.' },
        ],
    },
    {
        title: '16. Third-Party Sites and Tools',
        blocks: [
            { type: 'p', text: 'Our website includes embedded tools and links operated by third parties, such as video players and scheduling calendars. Their own privacy policies govern how they handle your information.' },
        ],
    },
    {
        title: '17. Changes to This Policy',
        blocks: [
            { type: 'p', text: 'We may update this policy from time to time. Changes will be posted on this page with an updated date.' },
        ],
    },
    {
        title: '18. Contact Us',
        blocks: [
            { type: 'p', text: 'White Glove Hiring, LLC' },
            { type: 'p', text: 'Email: adam@whiteglovehiring.com' },
            { type: 'p', text: 'Website: whiteglovehiring.com' },
        ],
    },
];

function PolicyBlock({ block }) {
    if (block.type === 'ul') {
        return (
            <ul className="list-disc pl-6 mb-4">
                {block.items.map((item, i) => (
                    <li key={i} className="text-[#6B7280] leading-relaxed mb-2 last:mb-0">{item}</li>
                ))}
            </ul>
        );
    }
    return <p className="text-[#6B7280] leading-relaxed mb-4 last:mb-0">{block.text}</p>;
}

export function PolicyContent() {
    return (
        <section className="bg-white py-24 md:py-32">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
                <motion.div {...enterUpDelayed(0.1)} data-testid="privacy-content">
                    <p className="text-[#6B7280] leading-relaxed mb-10">{intro}</p>
                    {sections.map((section) => (
                        <div key={section.title} className="mb-10">
                            <h2 className="font-montserrat font-bold text-xl text-navy-900 mb-3">{section.title}</h2>
                            {section.blocks.map((block, i) => (
                                <PolicyBlock key={i} block={block} />
                            ))}
                        </div>
                    ))}
                </motion.div>

                <div className="mt-12 pt-8 border-t border-gray-100">
                    <Link to="/contact" className="text-navy-900 text-sm font-semibold hover:text-gold-400 transition-colors" data-testid="privacy-contact-link">
                        Questions? Contact us
                    </Link>
                </div>
            </div>
        </section>
    );
}
